import React, {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import useFetchAllGenericResearchQueries from "../../../hooks/useFetchAllGenericResearchQueries";
import { Autocomplete, Chip, MenuItem, TextField } from "@mui/material";
import { CheckCircleRounded } from "@mui/icons-material";

interface IAutoCompleteGenericResearchQueryId {
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  handelOnChangeGenericResearchQuery: (
    event: ChangeEvent<any>,
    value: any,
  ) => void;
  currentGenericResearchQueryId:
    | string
    | number
    | null
    | Array<string | number>; // Support both single and multiple values
  sx?: React.CSSProperties;
  className?: string;
  isDisabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string;
  listLabel?: string;
  placeholder?: string;
  includeGenericResearchQueryId?: boolean; // Controls whether to include ID in display
  isMultiple?: boolean; // Controls whether multiple selection is enabled
  lazyLoad?: boolean; // ✅ NEW: Controls whether to fetch data on mount or on dropdown open
  // When defined, the underlying list call is filtered with
  // `is_score_config_associated=<bool>`. Used by the scoring add page to hide
  // queries that are already associated with a scoring configuration.
  filterByScoreConfigAssociated?: boolean;
}

/**
 * All generic research queries list with auto complete dropdown functionality
 * @returns
 * Selected generic research query with id and name
 */
const AutoCompleteGenericResearchQueryId: React.FC<
  IAutoCompleteGenericResearchQueryId
> = ({
  setIsLoading,
  handelOnChangeGenericResearchQuery,
  currentGenericResearchQueryId,
  sx,
  className,
  isDisabled = false,
  required,
  error,
  helperText,
  listLabel,
  placeholder,
  includeGenericResearchQueryId = false,
  isMultiple = false,
  lazyLoad = false, // ✅ NEW: Default to false for backward compatibility
  filterByScoreConfigAssociated,
}) => {
  // Track if dropdown has been opened (for lazy loading)
  const [hasOpened, setHasOpened] = useState(false);

  const {
    allGenericResearchQueries: genericResearchQueriesData,
    refetchQueries,
    isLoadingQueries,
  } = useFetchAllGenericResearchQueries({
    setIsLoading,
    fetchOnMount: !lazyLoad,
    isScoreConfigAssociated: filterByScoreConfigAssociated,
  });

  // Handle dropdown open - trigger fetch if lazy loading
  const handleOpen = useCallback(() => {
    if (lazyLoad && !hasOpened) {
      setHasOpened(true);
      refetchQueries();
    }
  }, [lazyLoad, hasOpened, refetchQueries]);

  // When lazy-loading with a preselected id (e.g., restored from URL),
  // fetch immediately so the selected option can be resolved on first render.
  useEffect(() => {
    const hasPreselected = Array.isArray(currentGenericResearchQueryId)
      ? currentGenericResearchQueryId.length > 0
      : !!currentGenericResearchQueryId;
    if (lazyLoad && !hasOpened && hasPreselected) {
      setHasOpened(true);
      refetchQueries();
    }
  }, [lazyLoad, hasOpened, currentGenericResearchQueryId, refetchQueries]);

  // Show loading spinner while fetching data
  const isLoading = lazyLoad && (isLoadingQueries || !hasOpened);

  // Compute the value based on single or multiple mode
  const computedValue = useMemo(() => {
    if (!genericResearchQueriesData || genericResearchQueriesData.length === 0)
      return isMultiple ? [] : null;

    if (isMultiple) {
      // Handle multiple selection mode
      if (Array.isArray(currentGenericResearchQueryId)) {
        return genericResearchQueriesData.filter((option) =>
          currentGenericResearchQueryId.includes(option.id),
        );
      }
      // If single value provided in multiple mode, convert to array
      if (currentGenericResearchQueryId) {
        const found = genericResearchQueriesData.find(
          (option) => option.id === currentGenericResearchQueryId,
        );
        return found ? [found] : [];
      }
      return [];
    } else {
      // Handle single selection mode
      return (
        genericResearchQueriesData.find(
          (option) => option.id === currentGenericResearchQueryId,
        ) || null
      );
    }
  }, [genericResearchQueriesData, currentGenericResearchQueryId, isMultiple]);

  // Generate label with or without ID
  const getLabel = (option: any) => {
    if (!option) return "";
    return includeGenericResearchQueryId
      ? `(${option.id}) ${option.name}`
      : option.name;
  };

  return (
    <Autocomplete
      sx={sx}
      fullWidth={isMultiple}
      multiple={isMultiple}
      disableCloseOnSelect={isMultiple}
      options={genericResearchQueriesData || []}
      value={computedValue}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      onOpen={handleOpen}
      loading={isLoading}
      renderInput={(params) =>
        isMultiple ? (
          <div className="autocomplete-chips-direction">
            <TextField
              style={{ color: "#000000" }}
              {...params}
              label={
                <span>
                  {listLabel ? listLabel : "Generic Research Query"}
                  {required ? <span className="required-asterisk">*</span> : ""}
                </span>
              }
              placeholder={placeholder ? placeholder : "Select..."}
              InputLabelProps={{
                shrink: true,
              }}
              error={!!error}
              helperText={helperText || ""}
            />
          </div>
        ) : (
          <TextField
            style={{ color: "#000000" }}
            {...params}
            label={
              <span>
                {listLabel ? listLabel : "Generic Research Query"}
                {required ? <span className="required-asterisk">*</span> : ""}
              </span>
            }
            placeholder={placeholder ? placeholder : "Select..."}
            InputLabelProps={{
              shrink: true,
            }}
            error={!!error}
            helperText={helperText || ""}
          />
        )
      }
      renderTags={
        isMultiple
          ? (value, getTagProps) => (
              <div className="align-from-left">
                {value.map((option: any, index: number) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={getLabel(option)}
                  />
                ))}
              </div>
            )
          : undefined
      }
      renderOption={
        isMultiple
          ? (props, option, { selected }) => (
              <MenuItem
                {...props}
                key={`${option?.id}-${option?.name}`}
                value={option?.id}
                sx={{ justifyContent: "space-between" }}
              >
                {getLabel(option)}
                {selected ? <CheckCircleRounded color="info" /> : null}
              </MenuItem>
            )
          : undefined
      }
      className={`${className}${
        isMultiple ? " form-control-autocomplete" : ""
      }`}
      loadingText="Loading..."
      onChange={(event, value) =>
        handelOnChangeGenericResearchQuery(event, value)
      }
      disabled={Boolean(isDisabled)}
    />
  );
};

export default AutoCompleteGenericResearchQueryId;
